import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";

import { heavyCard } from "../../utils/card/heavy";
import { heavy2Card } from "../../utils/card/heavy2";
import { lightCard } from "../../utils/card/light";
import { mediumCard } from "../../utils/card/medium";
import { tremendousCard } from "../../utils/card/tremendous";
import { negligibleCard } from "../../utils/card/negligible";
import { findFactionUnit } from "../../utils/faction";
import { buildFleetListCardObj } from "../../utils/fleetList";

import "./CardViewer.css";

const DEFAULT_META = {
  bgColor: "#d4c5a0",
  fgColor: "#ffffff",
  waterColor: "#7fb3c8",
  accentColor: "#c8a020",
  parchmentColor: "#f5e6b8",
};

function renderCard(meta, obj, inst) {
  switch (obj.weight) {
    case "heavy":
      return heavyCard(meta, obj, inst);
    case "heavy2":
      return heavy2Card(meta, obj, inst);
    case "light":
      return lightCard(meta, obj, inst);
    case "medium":
      return mediumCard(meta, obj, inst);
    case "tremendous":
      return tremendousCard(meta, obj, inst);
    case "negligible":
      return negligibleCard(meta, obj, inst);
    default:
      return null;
  }
}

function buildCrewCardObj(addonDef, list, cardObj) {
  const crewGroups = (list.crew || []).reduce((groups, entry) => {
    const key = entry.id;
    const existing = groups.find((g) => g.key === key);
    if (existing) {
      existing.count++;
    } else {
      groups.push({ key, entry, count: 1 });
    }
    return groups;
  }, []).sort((a, b) => a.count - b.count || a.entry.name.localeCompare(b.entry.name));

  const crewHtml = crewGroups.length === 0
    ? "<i>No crew assigned</i>"
    : crewGroups.map((group) => {
        const prefix = group.count > 1 ? `${group.count} ` : "";
        const pts = group.entry.cost === "-" ? "- pts" : `${Number(group.entry.cost) * group.count} pts`;
        const desc = group.entry.description
          ? `<div style="margin-left:12px;font-size:0.85em;"><i>${group.entry.description}</i></div>`
          : "";
        return `<div style="display:flex;justify-content:space-between;align-items:baseline;padding-right:16px;"><b>${prefix}${group.entry.name}</b><span style="font-size:0.85em;white-space:nowrap;">${pts}</span></div>${desc}`;
      }).join("");

  const totalCost = (list.crew || []).reduce((sum, c) => sum + (Number(c.cost) || 0), 0);

  const baseNotes = cardObj?.notes?.length > 0 ? cardObj.notes : null;
  const notes = baseNotes
    ? [{ ...baseNotes[0], note: { ...baseNotes[0].note, value: crewHtml } }, ...baseNotes.slice(1)]
    : [{ height: 100, title: { value: "DESCRIPTION", scale: 1.0 }, note: { value: crewHtml, scale: 1.0 } }];

  return {
    weight: "negligible",
    name: { value: (addonDef.name_en || addonDef.name || "Crew Card").toUpperCase(), scale: 1.0 },
    type: { value: (addonDef.type_name_en || addonDef.type_name || "Crew").toUpperCase(), scale: 0.75 },
    honors: { value: "", scale: 1.0 },
    cost: { value: `${totalCost} pts`, scale: 1.0 },
    notes,
  };
}

export const CardViewer = ({ listId, bwOverride, gridMode = "3x3" }) => {
  const lists = useSelector((state) => state.lists);
  const customFactions = useSelector((state) => state.customFactions);
  const customCards = useSelector((state) => state.customCards);
  const list = lists?.find((l) => l.id === listId);

  const rulesetId = list?.rulesetId;
  const factionId = list?.factionId;

  // Primary faction data — needed for crew_card detection
  const [factionData, setFactionData] = useState(null);

  useEffect(() => {
    if (!rulesetId || !factionId) {
      setFactionData(null);
      return;
    }
    fetch(`${process.env.PUBLIC_URL}/games/${rulesetId}/${factionId}.json`)
      .then((r) => r.json())
      .then(setFactionData)
      .catch(() => setFactionData(null));
  }, [rulesetId, factionId]);

  // metaByFaction: factionId -> meta object
  const [metaByFaction, setMetaByFaction] = useState({});
  // cardObjs: "factionId/cardId" -> card obj
  const [cardObjs, setCardObjs] = useState({});

  // Build unique "factionId/cardId" pairs for all non-blank cards.
  // Fall back to list.factionId for cards that predate per-card factionId tracking.
  const uniquePairsStr = [
    ...new Set(
      (list?.cards || [])
        .filter((c) => c.id !== "blank")
        .map((c) => `${c.factionId || factionId}/${c.id}`)
        .filter((key) => !key.startsWith("/"))
    ),
  ].sort().join(",");

  useEffect(() => {
    if (!rulesetId || !uniquePairsStr) {
      setCardObjs({});
      setMetaByFaction({});
      return;
    }

    const pairs = uniquePairsStr.split(",").map((key) => {
      const slashIdx = key.indexOf("/");
      return { factionId: key.slice(0, slashIdx), id: key.slice(slashIdx + 1) };
    });

    const uniqueFactionIds = [...new Set(pairs.map((p) => p.factionId))];

    const bwStylePromise = bwOverride
      ? fetch(`${process.env.PUBLIC_URL}/games/bw.json`).then((r) => r.json()).catch(() => ({}))
      : Promise.resolve(null);

    Promise.all(
      uniqueFactionIds.map(async (fId) => {
        const customFaction = customFactions.find((f) => f.id === fId && f.rulesetId === rulesetId);
        let styleData;
        if (bwOverride) {
          styleData = await bwStylePromise;
        } else if (customFaction) {
          styleData = customFaction.style && typeof customFaction.style === 'object' ? customFaction.style : {};
        } else {
          const fJson = await fetch(`${process.env.PUBLIC_URL}/games/${rulesetId}/${fId}.json`)
            .then((r) => r.json())
            .catch(() => ({}));
          styleData = fJson.style && typeof fJson.style === 'object' ? fJson.style : {};
        }
        return { factionId: fId, meta: { ...DEFAULT_META, ...styleData } };
      })
    ).then((results) => {
      const map = {};
      results.forEach(({ factionId: fId, meta }) => { map[fId] = meta; });
      setMetaByFaction(map);
    });

    // Fetch card JSONs; serve custom cards from state without fetching
    Promise.all(
      pairs.map(({ factionId: fId, id }) => {
        const key = `${fId}/${id}`;
        const customCard = customCards.find(
          (c) => c.factionId === fId && c.id === id && c.rulesetId === rulesetId
        );
        if (customCard) return Promise.resolve({ key, obj: customCard });
        return fetch(`${process.env.PUBLIC_URL}/games/${rulesetId}/${id}.json`)
          .then((r) => {
            if (!r.ok) return { key, obj: null };
            return r.json().then((obj) => ({ key, obj }));
          })
          .catch(() => ({ key, obj: null }));
      })
    ).then((results) => {
      const map = {};
      results.forEach(({ key, obj }) => { map[key] = obj; });
      setCardObjs(map);
    });
  }, [rulesetId, uniquePairsStr, bwOverride, customFactions, customCards]);

  const gridRef = useRef(null);
  const [cardScale, setCardScale] = useState(0.5);

  useEffect(() => {
    if (!gridRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cols = gridMode === "2x2" ? 2 : 3;
        const colWidth = (entry.contentRect.width - (cols - 1) * 3) / cols;
        setCardScale(colWidth / 500);
      }
    });
    observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, [gridMode]);

  const cols = gridMode === "2x2" ? 2 : 3;

  return (
    <div className="card-viewer" ref={gridRef} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {!listId && <p className="card-viewer__message">No list selected.</p>}
      {listId && !list?.cards?.length && <p className="card-viewer__message">No cards in this fleet.</p>}
      {listId && list?.cards?.flatMap((card, i) => {
        const count = card.number || 1;

        if (card.id === "blank") {
          return Array.from({ length: count }, (_, j) => (
            <div key={`blank-${i}-${j}`} className="card-viewer__card" />
          ));
        }

        const cardFactionId = card.factionId || factionId;
        const meta = metaByFaction[cardFactionId];

        // Dynamic crew card — build obj from list.crew, merging into fetched card JSON notes
        const addonDef = findFactionUnit(factionData, card.id);

        if (addonDef?.type === "fleet_list_card") {
          const obj = buildFleetListCardObj(addonDef, list, factionData);
          const html = meta ? DOMPurify.sanitize(negligibleCard(meta, obj, { dynamicBoxes: true }), { ADD_ATTR: ['contenteditable'] }) : null;
          return Array.from({ length: count }, (_, j) => (
            <div key={`${card.uid || `${card.id}-${i}`}-${j}`} className="card-viewer__card">
              {html ? (
                <div
                  className="card-viewer__card-inner"
                  style={{ transform: `scale(${cardScale})` }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <div className="card-viewer__placeholder">{card.name || card.id}</div>
              )}
            </div>
          ));
        }

        if (addonDef?.type === "crew_card") {
          const fetchedCrewJson = cardObjs[`${cardFactionId}/${card.id}`];
          const crewMeta = !bwOverride && fetchedCrewJson?.styleOverride
            ? { ...DEFAULT_META, ...fetchedCrewJson.styleOverride }
            : meta;
          const obj = buildCrewCardObj(addonDef, list, fetchedCrewJson);
          const html = crewMeta ? DOMPurify.sanitize(negligibleCard(crewMeta, obj, { dynamicBoxes: true }), { ADD_ATTR: ['contenteditable'] }) : null;
          return Array.from({ length: count }, (_, j) => (
            <div key={`${card.uid || `${card.id}-${i}`}-${j}`} className="card-viewer__card">
              {html ? (
                <div
                  className="card-viewer__card-inner"
                  style={{ transform: `scale(${cardScale})` }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <div className="card-viewer__placeholder">{card.name || card.id}</div>
              )}
            </div>
          ));
        }

        const obj = cardObjs[`${cardFactionId}/${card.id}`];
        const effectiveObj = obj && card.randomOverride ? { ...obj, ...card.randomOverride } : obj;
        const effectiveMeta = !bwOverride && obj?.styleOverride ? { ...DEFAULT_META, ...obj.styleOverride } : meta;
        const isDouble = effectiveObj?.weight === "heavy2";
        const cardStyle = isDouble ? { gridColumn: "span 2", aspectRatio: "10 / 7" } : undefined;
        return Array.from({ length: count }, (_, j) => {
          const shipName = (card.shipNames || [])[j];
          const inst = { dynamicBoxes: true, ...(shipName ? { name: { value: shipName, scale: 1.0 } } : {}) };
          const html = effectiveObj && effectiveMeta ? DOMPurify.sanitize(renderCard(effectiveMeta, effectiveObj, inst), { ADD_ATTR: ['contenteditable'] }) : null;
          return (
            <div key={`${card.uid || `${card.id}-${i}`}-${j}`} className="card-viewer__card" style={cardStyle}>
              {html ? (
                <div
                  className="card-viewer__card-inner"
                  style={{ transform: `scale(${cardScale})` }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <div className="card-viewer__placeholder">
                  {obj === undefined ? "Loading..." : (card.name || card.id)}
                </div>
              )}
            </div>
          );
        });
      })}
    </div>
  );
};
